import googlePalette from 'google-palette';

/**
 * Tipos e valores.
 *
 * Os gráficos poligonais são feitos para suportar desde uma visualização simples, de um gráfico
 * com um ponto, até visualizações complexas de múltiplos gráficos com múltiplos pontos.
 *
 * Um tipo de gráfico é um valor do enum @/domain/enums/polygonalCharts/polygonalChart.js.
 * Um valor é um objeto no formato: { x: number, y: number }.
 * Opcionalmente um valor pode conter name para exibição de legenda: { x, y, name: string }.
 * Por padrão a legenda é exibida.
 *
 * Veja abaixo as diferentes opções de configuração do gráfico e suas visualizações:
 *
 * Um gráfico com um ponto:
 * - type: string de enum de tipo. Exemplo: polygonalCharts.DUVAL_TRIANGLE_1
 * - value: 1 objeto de valor ou uma array de objetos de valor.
 *     Exemplo: { x: 1, y: 4.5} ou [{ x: 1, y: 4.5}].
 *
 * Um gráfico com múltiplos pontos.
 * - type: string de enum de tipo. Exemplo: polygonalCharts.DUVAL_PENTAGON_2
 * - value: array de objetos de valor
 *     Exemplo: [{ x: 1, y: 4.5}, { x: 15, y: 35 }].
 *
 * Múltiplos gráficos com múltiplos pontos.
 * - type: array de strings de enum de tipo. Exemplo:
 *     [polygonalCharts.GASES_RATIO_C2H2_C2H4_CH4_H2,
 *     polygonalCharts.GASES_RATIO_C2H2_C2H4_C2H4_C2H6]
 * - value: array de array de objetos de valor.
 *     Exemplo: [arrayValoresTipo1[], arrayValoresTipo2[], ....]
 *
 * Note que no caso de múltiplos gráficos com multiplos pontos, a array multidimensional de pontos
 * deve ter o mesmo tamanho que a array de tipos, ou seja, para 2 tipos, então value deve ser uma
 * array de 2 elementos, sendo cada elemento uma array e valores para cada tipo respectivamente.
 *
 * Qualquer input de dados diferente dos formatos descritos acima irá resultar e excessão de código.
 *
 * A legenda do gráfico é única, ou seja, em caso de múltiplos gráficos, os valores com o mesmo
 * "name" irão compartilhar a mesma cor e legenda.
 *
 * Por padrão, a legenda é habilitada. Quando a legenda está habilitada, todos os valores DEVEM
 * possuir a propriedade "name" preenchida. Para desativar a legenda, defina `no-subtitle`.
 *
 */

/**
 * @typedef {Object} ChartValue
 * @property {Number} x Valor de X.
 * @property {Number} y Valor de Y.
 * @property {String} [name] Nome do valor para tooltip e legenda. Por padrão a legenda é
 * habilitada, e quando habilitada, `name` é obrigatório.
 */

/**
 * @private
 * @typedef {Object} ColoredChartValue Valor com Cor.
 * @property {Number} x Valor de X.
 * @property {Number} y Valor de Y.
 * @property {String} [name] Nome do valor.
 * @property {String} [color=#000000] Cor do Valor.
 */

/**
 * Prefixo de identidade de valor.
 */
const valueIdentifierPrefix = '$valueId';

/**
 * Retorna o identificador de um valor com base em sua posição.
 */
function getValueIdentifier(typeIndex, valueIndex) {
  return `${valueIdentifierPrefix}_${typeIndex}_${valueIndex}`;
}

/**
 * Testa se o valor informado é um identificador de valor de gráfico.
 */
function isValueIdentifier(value) {
  return value.startsWith(valueIdentifierPrefix);
}

/**
 * Retorna o Id de identificação de uma cor. Se o valor de gráfico possuir "name", então esse é o
 * identificador. Caso contrário, o identificador é gerado com base no índice do tipo e do valor.
 * Note que "name"s iguals entre valores diferentes irão resultar no mesmo Id de cor e isso é
 * intencional para que a legenda seja consistente. Por outro lado, valores sem "name" terão um
 * indentificador único, ou seja, não irão se misturar aos outros, tendo a mesma cor.
 * @param {Object} value Valor do gráfico.
 * @param {Number} typeIndex Índice do tipo do gráfico.
 * @param {Number} valueIndex Índice do valor na array de valores.
 * @returns {String} Id gerado da cor.
 */
function getColorId({ name }, typeIndex, valueIndex) {
  return name != null ? name : getValueIdentifier(typeIndex, valueIndex);
}

/**
 * Gráfico poligonal.
 */
export default {
  props: {
    /**
     * Valor ou array de valores ou array multi-dimensional de valores para o gráfico.
     * Veja a documentação acima do componente para entender a entrada de valores.
     * @type {ChartValue|ChartValue[]|ChartValue[][]}
     */
    value: {
      type: [Object, Array],
    },
    /**
     * Tipo ou array de tipos do gráfico.
     * Veja a documentação acima do componente para entender a entrada de tipos.
     * @type {enum}
     * Ver @/domain/enums/polygonalCharts/polygonalChart.js
     */
    type: {
      type: [String, Array],
      required: true,
    },
    /**
     * Remover legenda.
     */
    noSubtitle: {
      type: Boolean,
      default: false,
    },
    /**
     * Tamanho do gráfico.
     * Suportados:
     * - small
     * - medium (default)
     */
    size: {
      type: String,
      default: 'medium',
    },
    /**
     * Coordenadas de referência. Pode ser usado para manter um padrão de cores de itens
     * da legenda, caso o gráfico seja complementar a outro e possuam as mesmas legendas.
     */
    referenceCoordinates: {
      type: Array,
    },
  },
  computed: {
    /**
     * Pega o valor informado na property `type` e retorno sempre como array de valores.
     */
    types() {
      if (Array.isArray(this.type)) {
        return this.type;
      }

      return [this.type];
    },
    /**
     * Retorna se é um gráfico multiplo.
     */
    isMultiple() {
      return this.types.length > 1;
    },
    /**
     * Pega o valor informado em `value` e retorna sempre uma array multidimensional.
     * Se o gráfico for de apenas um tipo, então a array multidimensional terá apenas 1 subarray.
     * @returns {ChartValue[][]}
     */
    multiValues() {
      if (this.value == null) {
        return this.types.map(() => []);
      }

      if (Array.isArray(this.value)) {
        return this.getMultiValueByArray();
      }

      if (this.types.length === 1) {
        return [[this.value]];
      }

      throw new Error('O valor fornecido em "value" não é suportado para o(s) tipo(s) de gráfico '
        + `informados em "type": ${this.type}. Veja a documentação de "type" e "value" para `
        + 'entender os formatos de entrada.');
    },
    /**
     * Cria um dicionário de cores para os valores.
     * As cores são identificas pelo `name` dos valores, ou caso não haja name, então um
     * identificador único é gerado (@see getColorId).
     * @returns {Object} Dicionário de cores por identificador.
     */
    valueColorsDictionary() {
      const valueColors = {};
      const currentCoordinatesKeys = this.getKeysFromCoordinates(this.multiValues);
      const referenceCoordinatesKeys = this.getKeysFromCoordinates([this.referenceCoordinates]);
      const colorsNumber = referenceCoordinatesKeys.length > 0
        ? referenceCoordinatesKeys.length
        : currentCoordinatesKeys.length;
      const colorPalette = googlePalette('rainbow', colorsNumber);

      if (referenceCoordinatesKeys.length > 0) {
        referenceCoordinatesKeys.forEach((value, index) => {
          if (currentCoordinatesKeys.includes(value)) {
            valueColors[value] = `#${colorPalette[index]}`;
          }
        });

        return valueColors;
      }

      currentCoordinatesKeys.forEach((value, index) => {
          valueColors[value] = `#${colorPalette[index]}`;
      });

      return valueColors;
    },
    /**
     * Retorna uma coleção de valores de cores para a legenda.
     * @returns {{ name: String, color: String }[]}
     */
    subtitleValueColors() {
      return Object.keys(this.valueColorsDictionary)
        .filter(key => !isValueIdentifier(key))
        .sort((a, b) => +a - +b)
        .map(key => ({
          name: key,
          color: this.valueColorsDictionary[key],
        }));
    },
    /**
     * Retorna as visualizações de gráficos.
     * Haverá uma visualização de gráfico por tipo informado.
     * Cada visualização será renderizada em um gráfico diferente com seus valores.
     * @returns {{ type: String, value: ColoredChartValue[] }[]}
     */
    chartViews() {
      return this.types.map((type, typeIndex) => ({
        type,
        value: this.multiValues[typeIndex].map((value, valueIndex) => ({
          ...value,
          color: this.resolveColor(value, typeIndex, valueIndex),
        })),
      }));
    },
    /**
     * Modificadores de estilização para os gráficos.
     */
    chartModifiers() {
      return {
        [`polygonal-chart__svg-wrapper_${this.size}`]: true,
        'polygonal-chart__svg-wrapper_multiple': this.isMultiple,
      };
    },
  },
  methods: {
    getValueByMultipleChart() {
      if (this.value.length === this.types.length
        && this.value.every(value => Array.isArray(value))) {
        return this.value;
      }

      throw new Error('Quando múltiplos gráficos são informados em "type", então "value" deve'
        + ' ser um array multidimensional com uma sub-array para cada gráfico/tipo.');
    },
    getMultiValueByArray() {
      if (this.isMultiple) {
        return this.getValueByMultipleChart();
      }
      return [this.value];
    },
    getKeysFromCoordinates(coordinates) {
      const distinctValues = new Set();
      (coordinates || []).forEach((items, typeIndex) => (items || [])
      .forEach(({ name }, valueIndex) => {
        const colorId = getColorId({ name }, typeIndex, valueIndex);
        distinctValues.add(colorId);
      }));
      return [...distinctValues];
    },
    /**
     * Resolve uma cor a partir das informações de um valor, retornando a cor.
     * @returns {String} Cor.
     */
    resolveColor(value, typeIndex, valueIndex) {
      return this.valueColorsDictionary[getColorId(value, typeIndex, valueIndex)];
    },
  },
};
